import {
  STATUS_MATCH_EVIDENCE_BASE,
  buildChallengeCardSubtitle,
  buildChallengeCardTitle,
  serializeChallengeCardBackground,
} from './challengeCard';
import { resolveProgramDisplayName } from './programs';
import { normalizeCampaignSlug } from './slug';
import type { PayloadBuilderState, ProgramConfig } from './types';

function nonEmptyStrings(values: Array<string>): Array<string> {
  return values.map((value) => value.trim()).filter(Boolean);
}

export function buildPayload(state: PayloadBuilderState): Record<string, unknown> {
  if (state.mode === 'statusClaim') {
    return buildStatusClaimPayload(state);
  }
  return buildStatusMatchPayload(state);
}

function buildStatusClaimPayload(state: PayloadBuilderState): Record<string, unknown> {
  return {
    campaignId: state.campaignId.trim(),
    slug: normalizeCampaignSlug(state.slug),
    termsSlug: state.termsSlug.trim(),
    hero: {
      title: state.heroTitle.trim(),
      subtitle: state.heroSubtitle.trim(),
      imageId: state.heroImageId.trim(),
    },
    partner: {
      name: state.partnerName.trim(),
      logoImageId: state.partnerLogoImageId.trim(),
      memberStatus: 'member',
    },
    tiers: [...state.tiersGranted],
    secondarySection: {
      title: state.secondaryTitle.trim(),
      subtitle: state.secondarySubtitle.trim(),
    },
  };
}

function mapProgramTiers(program: ProgramConfig): Array<{ sourceTier: string; targetSocieteTier: string }> {
  return program.tierMappings
    .filter((mapping) => mapping.sourceTier.trim() && mapping.targetSocieteTier.trim())
    .map((mapping) => ({
      sourceTier: mapping.sourceTier.trim(),
      targetSocieteTier: mapping.targetSocieteTier.trim(),
    }));
}

function buildStatusMatchPayload(state: PayloadBuilderState): Record<string, unknown> {
  return {
    slug: normalizeCampaignSlug(state.slug),
    campaignId: state.campaignId.trim(),
    ...(state.allowedRegions !== undefined ? { allowedRegions: [...state.allowedRegions] } : {}),
    hero: {
      title: state.heroTitle.trim(),
      subtitle: state.heroSubtitle.trim(),
      description: state.heroDescription.trim(),
      ctaText: state.heroCtaText.trim(),
      imageId: state.heroImageId.trim(),
    },
    programs: state.programs
      .filter((program) => program.programName.trim())
      .map((program) => ({
        name: resolveProgramDisplayName(program.programName.trim()),
        logoImageId: program.logoImageId.trim(),
        tiers: mapProgramTiers(program),
      })),
    evidence: {
      ...STATUS_MATCH_EVIDENCE_BASE,
      minPhotos: state.evidenceMinPhotos,
      photoHints: nonEmptyStrings(state.evidencePhotoHints),
      acceptedFormats: nonEmptyStrings(state.evidenceAcceptedFormats),
      proofRequirements: nonEmptyStrings(state.evidenceProofRequirements),
      verificationImageId: state.evidenceVerificationImageId.trim(),
    },
    conditions: state.conditions
      .filter((condition) => condition.title || condition.description)
      .map((condition) => ({
        icon: condition.icon,
        title: condition.title,
        description: condition.description,
      })),
    howItWorks: state.howItWorks
      .filter((step) => step.title || step.description)
      .map((step) => ({
        title: step.title,
        description: step.description,
      })),
    challengeCard: {
      background: serializeChallengeCardBackground(state.challengeCardBackground),
      cardTitle: buildChallengeCardTitle(state.challengeCardTitleSuffix),
      cardSubtitle: buildChallengeCardSubtitle(state.challengeCardSubtitleBefore, state.challengeCardSubtitleAfter),
    },
    successSteps: state.successSteps.map((step) => ({
      key: step.key,
      title: step.title,
      description: step.description,
    })),
    faqs: state.faqs
      .filter((faq) => faq.question || faq.answer)
      .map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      })),
    terms: nonEmptyStrings(state.terms),
    termsSlug: state.termsSlug.trim(),
  };
}

export function serializePayload(state: PayloadBuilderState): string {
  return JSON.stringify(buildPayload(state), null, 2);
}

export function payloadDownloadFilename(state: PayloadBuilderState): string {
  const slug = state.slug.trim();
  return slug ? `${slug}-payload.txt` : 'campaign-payload.txt';
}
